import { useCallback, useEffect, useMemo, useRef } from 'react'
import debounce from 'lodash/debounce'
import { usePopover } from 'gu-ui-kit/Popover'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'

export const DRAWER_FOOTER_POPOVER_MIN_WIDTH = 768

export const DRAWER_FOOTER_POPOVER_CLOSE_DELAY_MS = 150

export const DRAWER_FOOTER_POPOVER_BREAKPOINTS = {
  0: {
    variant: 'bottomSheet',
    hasBackdrop: true,
  },
  [DRAWER_FOOTER_POPOVER_MIN_WIDTH]: {
    variant: 'popover',
    hasBackdrop: false,
  },
}

/** collapsed desktop popover → hover; expanded desktop / bottomSheet → click */
export const shouldUseDrawerFooterHoverMode = (isPopoverViewport, collapse) =>
  Boolean(isPopoverViewport && collapse)

export const getDrawerFooterInteractionConfig = ({ isPopoverViewport, collapse }) => {
  const useHoverMode = shouldUseDrawerFooterHoverMode(isPopoverViewport, collapse)

  return {
    useHoverMode,
    isPopoverViewport: Boolean(isPopoverViewport),
    popoverEvents: useHoverMode ? DRAWER_FOOTER_HOVER_POPOVER_EVENTS : {},
    popoverConfig: isPopoverViewport ? getDrawerFooterPopoverConfig(collapse) : {},
  }
}

export const DRAWER_FOOTER_POPOVER_OFFSET = 8

export const DRAWER_FOOTER_EXPANDED_POPOVER_CONFIG = {
  anchorOrigin: { vertical: 'top', horizontal: 'left' },
  transformOrigin: { vertical: DRAWER_FOOTER_POPOVER_OFFSET, horizontal: 'left' },
  stretch: true,
}

export const DRAWER_FOOTER_HOVER_POPOVER_EVENTS = {
  triggerOpenEventName: 'onMouseEnter',
  triggerCloseEventName: 'onMouseLeave',
}

export const getDrawerFooterPopoverConfig = (collapse) => {
  if (collapse) {
    return {
      anchorOrigin: { vertical: 'top', horizontal: 'right' },
      transformOrigin: { vertical: 'top', horizontal: DRAWER_FOOTER_POPOVER_OFFSET },
      stretch: false,
    }
  }

  return DRAWER_FOOTER_EXPANDED_POPOVER_CONFIG
}

export const getDrawerFooterAnchorEl = (
  footerAnchorRef,
  triggerEl,
  collapse,
  { useFooterAnchor = true } = {},
) => {
  if (collapse || !useFooterAnchor) {
    return triggerEl
  }

  return (
    footerAnchorRef?.current ??
    document.querySelector('[data-drawer-footer-anchor]') ??
    triggerEl
  )
}

export const bindDrawerFooterTrigger = (
  bindTrigger,
  popoverState,
  {
    collapse,
    footerAnchorRef,
    useFooterAnchor = true,
    useHoverMode = false,
    onScheduleClose,
    onCancelScheduledClose,
  },
) => {
  const bindings = bindTrigger(popoverState)

  const openWithAnchor = (event) => {
    event.stopPropagation()
    if (onCancelScheduledClose) {
      onCancelScheduledClose()
    }

    const anchorEl = getDrawerFooterAnchorEl(footerAnchorRef, event.currentTarget, collapse, {
      useFooterAnchor,
    })

    if (!anchorEl) {
      return
    }

    popoverState.open({
      id: popoverState.id,
      anchorEl,
    })
  }

  if (useHoverMode) {
    const { onMouseEnter: _onMouseEnter, onMouseLeave: _onMouseLeave, ...rest } = bindings

    return {
      ...rest,
      onMouseEnter: openWithAnchor,
      onMouseLeave: () => {
        if (onScheduleClose) {
          onScheduleClose()
        }
      },
    }
  }

  const { onClick: _onClick, ...rest } = bindings

  return {
    ...rest,
    onClick: (event) => {
      if (popoverState.isOpen) {
        event.stopPropagation()
        popoverState.close()
        return
      }

      openWithAnchor(event)
    },
  }
}

export const useDrawerFooterMenuInteractions = ({ collapse, footerAnchorRef, id }) => {
  const breakpoints = useBreakpoint()
  const { useHoverMode, isPopoverViewport, popoverEvents, popoverConfig } = useMemo(
    () =>
      getDrawerFooterInteractionConfig({
        isPopoverViewport: breakpoints.fb,
        collapse,
      }),
    [breakpoints.fb, collapse],
  )
  const { bindTrigger, bindPopover, usePopupState } = usePopover(popoverEvents)
  const popoverState = usePopupState({
    variant: 'popover',
    id,
  })

  const closeRef = useRef(popoverState.close)
  closeRef.current = popoverState.close

  const scheduleCloseRef = useRef(null)

  useEffect(() => {
    scheduleCloseRef.current = debounce(() => {
      closeRef.current()
    }, DRAWER_FOOTER_POPOVER_CLOSE_DELAY_MS)

    return () => {
      if (scheduleCloseRef.current) {
        scheduleCloseRef.current.cancel()
      }
    }
  }, [])

  const cancelScheduledClose = useCallback(() => {
    if (scheduleCloseRef.current) {
      scheduleCloseRef.current.cancel()
    }
  }, [])

  const scheduleClose = useCallback(() => {
    if (scheduleCloseRef.current) {
      scheduleCloseRef.current()
    }
  }, [])

  const handleContentMouseEnter = useCallback(() => {
    popoverState.lock()
    cancelScheduledClose()
  }, [cancelScheduledClose, popoverState])

  const handleContentMouseLeave = useCallback(() => {
    popoverState.lock()
    scheduleClose()
  }, [popoverState, scheduleClose])

  const triggerBindings = useMemo(
    () =>
      isPopoverViewport
        ? bindDrawerFooterTrigger(bindTrigger, popoverState, {
            collapse,
            footerAnchorRef,
            useHoverMode,
            onScheduleClose: scheduleClose,
            onCancelScheduledClose: cancelScheduledClose,
          })
        : bindTrigger(popoverState),
    [
      bindTrigger,
      cancelScheduledClose,
      collapse,
      footerAnchorRef,
      isPopoverViewport,
      popoverState,
      scheduleClose,
      useHoverMode,
    ],
  )

  const popoverAnchorEl = useMemo(() => {
    if (!isPopoverViewport || collapse) {
      return popoverState.anchorEl
    }

    return (
      footerAnchorRef?.current ??
      document.querySelector('[data-drawer-footer-anchor]') ??
      popoverState.anchorEl
    )
  }, [collapse, footerAnchorRef, isPopoverViewport, popoverState.anchorEl, popoverState.isOpen])

  const popoverBindings = useMemo(
    () => ({
      ...bindPopover(popoverState),
      ...(isPopoverViewport ? { anchorEl: popoverAnchorEl } : {}),
      ...popoverConfig,
      breakpoints: DRAWER_FOOTER_POPOVER_BREAKPOINTS,
    }),
    [bindPopover, isPopoverViewport, popoverAnchorEl, popoverConfig, popoverState],
  )

  const contentHoverHandlers = useMemo(
    () =>
      useHoverMode
        ? {
            onMouseEnter: handleContentMouseEnter,
            onMouseLeave: handleContentMouseLeave,
          }
        : {},
    [handleContentMouseEnter, handleContentMouseLeave, useHoverMode],
  )

  return {
    useHoverMode,
    isPopoverViewport,
    popoverState,
    triggerBindings,
    popoverBindings,
    contentHoverHandlers,
  }
}
